import React, { FC, ReactElement, useEffect, useState } from "react";
import Modal from 'react-modal';
import { ENROLL_TYPE, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../constants/command-status";
import { EnrolSubCmds } from "../../constants/sub-commands";
import { WS_URL } from "../../constants/ws-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../../contexts/websocket/IcaWebsocketContext";
import { EnrolmentTask } from "../../models/EnrolmentTask";
import { ExecuteCommand } from "../../models/ExecuteCommand";
import { Person } from "../../models/Person";
import { Tag } from "../../models/Tag";
import WizardSideBarStep from "../../models/WizardSideBarStep";
import { requestEnrolDevice } from "../../services/executeCommandService";
import { IShowEnrollValidOptions } from "../../services/wsPluginService/plugins/base/pluginModels";
import { checkPluginValidatedList, isPluginValidationListInterface } from "../../services/wsPluginService/plugins/decodePluginMsg";
import { validatePluginMsg } from "../../services/wsPluginService/plugins/validatePluginMsg";
import WSPluginService from "../../services/wsPluginService/wsPluginService";
import Wizard from "../wizard/wizard";
import EncodeCardWizard from "./encode/encode-card-wizard";
import EnrolCardWizard from "./card/enrol-card-wizard";
import EditCardWizard from "./card/edit-card-wizard";
import EnrolFaceWizard from "./face/enrol-face-wizard";
import EnrolFingerWizard from "./finger/enrol-finger-wizard";
import EnrolHandWizard from "./hand/enrol-hand-wizard";
import EnrolMobileWizard from "./mobile/enrol-mobile-wizard";
import EnrolQrCodeWizard from "./qr-code/enrol-qrcode-wizard";
import TasksForm, {
    BIOMETRIC_BRAND_CONTROL_ID,
    BIOMETRIC_BRAND_HID,
    BIOMETRIC_BRAND_HIKVISION,
    BIOMETRIC_BRAND_IDEMIA,
    BIOMETRIC_TASK_CATEGORY_VALUE
} from "./tasks-form";

type Props = {
    onClose: () => void
    isOpen: boolean
    onCardSave: (card: Tag) => void,
    currentCredential: Tag,
    currentItem: Person;
    onRequestMobileCredential: (values: any) => void;
    updateBadge: (badge: number) => void;
    isVingcardAccountAvailable: boolean;
};

const EnrolWizard: FC<Props> = ({
    onClose,
    isOpen,
    onCardSave,
    currentCredential,
    currentItem,
    onRequestMobileCredential,
    updateBadge,
    isVingcardAccountAvailable
}) => {
    const { translate } = useLocale();
    const defaultPluginsAvailable: IShowEnrollValidOptions = { mde: false, camera: false, print: false, omnikey: false, morpho: false, suprema: false, nitgen: false, extension: false, lumidigm: false, controlid: false };
    const [taskId, setTaskId] = useState<number>(0);
    const [taskDescription, setTaskDescription] = useState<string>('Pending');
    const [taskSelectionStep, setTaskSelectionStep] = useState<number>(0);
    const [selectedBiometricBrand, setSelectedBiometricBrand] = useState<string>('');
    const [pluginsAvailable, setPluginsAvailable] = useState<IShowEnrollValidOptions>(defaultPluginsAvailable);
    const [availableSystemDeviceIds, setAvailableSystemDeviceIds] = useState<number[]>([]);
    const [pluginWSInstance, setPluginWSInstance] = useState<WebSocket | undefined>();
    const [isPluginAvailabilityLoaded, setIsPluginAvailabilityLoaded] = useState<boolean>(false);
    const [isSystemDeviceAvailabilityLoaded, setIsSystemDeviceAvailabilityLoaded] = useState<boolean>(false);
    const enrolmentTask = new EnrolmentTask(translate);
    const icaWebSocket = useIcaWebsocketContext();
    const [hasTags, setHasTags] = useState<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            setTaskId(0);
            setTaskDescription("Pending");
            setTaskSelectionStep(0);
            setSelectedBiometricBrand('');
            setAvailableSystemDeviceIds([]);
            setPluginsAvailable(defaultPluginsAvailable);
            setIsPluginAvailabilityLoaded(false);
            setIsSystemDeviceAvailabilityLoaded(false);
        }

        setHasTags(currentItem.totalTags > 0);
    }, [isOpen, currentItem.totalTags]);

    useEffect(() => {
        if (!isOpen || !icaWebSocket.connectionState) {
            return;
        }

        icaWebSocket.startStopTopic(
            MONITORING_COMMAND.MONITORING_ENROL,
            MONITORING_COMMAND.MONITORING,
            MONITORING_COMMAND_STATE.MONITORING_START
        );

        return () => {
            icaWebSocket.startStopTopic(
                MONITORING_COMMAND.MONITORING_ENROL,
                MONITORING_COMMAND.MONITORING,
                MONITORING_COMMAND_STATE.MONITORING_STOP
            );
        };
    }, [isOpen, icaWebSocket.connectionState]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        revalidatePluginAvailability();
        refreshDeviceAvailability();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !pluginWSInstance || pluginWSInstance.readyState !== WebSocket.OPEN) {
            return;
        }

        // Run a fresh availability pass as soon as plugin WS is truly ready.
        revalidatePluginAvailability();
        refreshDeviceAvailability();
    }, [isOpen, pluginWSInstance]);

    const closeWizard = () => {
        icaWebSocket.startStopTopic(
            MONITORING_COMMAND.MONITORING_ENROL,
            MONITORING_COMMAND.MONITORING,
            MONITORING_COMMAND_STATE.MONITORING_STOP
        );
        onClose();
    };

    const resetKeyField = () => {
        setTaskId(0);
        setTaskDescription("Pending");
        setTaskSelectionStep(0);
        setSelectedBiometricBrand('');
    };

    const refreshDeviceAvailability = async () => {
        const command: ExecuteCommand = {
            cmd: ENROLL_TYPE.ENROLL,
            cmdSubType: EnrolSubCmds.Enroll_Devices_Available
        };

        setIsSystemDeviceAvailabilityLoaded(false);
        try {
            const response = await Promise.race([
                requestEnrolDevice(command),
                new Promise<never>((_, reject) => {
                    setTimeout(() => reject(new Error("request-enrol-device-timeout")), 8000);
                })
            ]);
            setAvailableSystemDeviceIds(response?.ids ?? []);
        } catch {
            setAvailableSystemDeviceIds([]);
        } finally {
            setIsSystemDeviceAvailabilityLoaded(true);
        }
    };

    const revalidatePluginAvailability = () => {
        setIsPluginAvailabilityLoaded(false);

        if (pluginWSInstance && pluginWSInstance.readyState === WebSocket.OPEN) {
            pluginWSInstance.send(validatePluginMsg());
        }
    };

    const onDetailsNext = (event: any) => {
        if (event.task) {
            if (event.task === BIOMETRIC_TASK_CATEGORY_VALUE) {
                setTaskDescription(translate.byKey(TranslationKeys.selectBrand));
                setTaskSelectionStep(1);
                return;
            }

            if (
                event.task === BIOMETRIC_BRAND_CONTROL_ID ||
                event.task === BIOMETRIC_BRAND_IDEMIA ||
                event.task === BIOMETRIC_BRAND_HID ||
                event.task === BIOMETRIC_BRAND_HIKVISION
            ) {
                setSelectedBiometricBrand(event.task);
                setTaskDescription(event.description);
                setTaskSelectionStep(2);
                return;
            }

            setTaskId(event.id);
            setTaskDescription(selectedBiometricBrand ? `${taskDescription} - ${event.description}` : event.description);
        }
    };

    const onBiometricBrandBack = () => {
        setTaskSelectionStep(0);
        setSelectedBiometricBrand('');
        setTaskDescription("Pending");
    };

    const onBiometricModalityBack = () => {
        setTaskSelectionStep(1);
        setTaskDescription(translate.byKey(TranslationKeys.selectBrand));
    };

    const getSelectedBiometricBrandKey = (): string | undefined => {
        if (selectedBiometricBrand === BIOMETRIC_BRAND_CONTROL_ID) return 'controlid';
        if (selectedBiometricBrand === BIOMETRIC_BRAND_IDEMIA) return 'idemia';
        if (selectedBiometricBrand === BIOMETRIC_BRAND_HID) return 'hid';
        if (selectedBiometricBrand === BIOMETRIC_BRAND_HIKVISION) return 'hikvision';
        return undefined;
    };

    const availabilityResolved = isPluginAvailabilityLoaded && isSystemDeviceAvailabilityLoaded;

    const defaultContent: ReactElement[] = [
        <TasksForm
            mode="main"
            pluginDevice={pluginsAvailable}
            availableSystemDeviceIds={availableSystemDeviceIds}
            availabilityResolved={availabilityResolved}
            isVingcardAccountAvailable={isVingcardAccountAvailable}
            onNext={onDetailsNext} />,
    ];

    const taskSelectionContent: ReactElement[] = [
        ...defaultContent,
        <TasksForm
            mode="biometric-brand"
            pluginDevice={pluginsAvailable}
            availableSystemDeviceIds={availableSystemDeviceIds}
            availabilityResolved={availabilityResolved}
            onBack={onBiometricBrandBack}
            onNext={onDetailsNext} />,
        <TasksForm
            mode="biometric-modality"
            selectedBiometricBrand={selectedBiometricBrand}
            pluginDevice={pluginsAvailable}
            availableSystemDeviceIds={availableSystemDeviceIds}
            availabilityResolved={availabilityResolved}
            onBack={onBiometricModalityBack}
            onNext={onDetailsNext} />,
    ];

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.tasks),
            description: taskDescription,
            substeps: []
        }
    ];

    const getWizard = () => {
        if (currentCredential.id !== 0) {
            return <EditCardWizard
                cardInfo={currentCredential}
                onClose={onClose}
                onSave={onCardSave} />;
        }

        if (taskId === enrolmentTask.ENROL_QR_CODE.id) {
            return <EnrolQrCodeWizard
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                hasTags={hasTags}
                masterId={currentItem.id} />;
        }

        if (taskId === enrolmentTask.ENROL_CARD.id) {
            return <EnrolCardWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                availabilityResolved={availabilityResolved}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                websocket={pluginWSInstance}
                onSave={onCardSave} />;
        }

        if (taskId === enrolmentTask.ENROL_MOBILE_CREDENTIAL.id) {
            return <EnrolMobileWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                availabilityResolved={availabilityResolved}
                cardInfo={currentCredential}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onFieldReset={resetKeyField}
                onSave={onRequestMobileCredential}
                onClose={closeWizard}
                userEmail={currentItem.email} />;
        }

        if (taskId === enrolmentTask.ENROL_FINGERPRINT.id) {
            return <EnrolFingerWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                availabilityResolved={availabilityResolved}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                websocket={pluginWSInstance}
                updateBadge={updateBadge}
                masterId={currentItem.id}
                selectedBiometricBrand={getSelectedBiometricBrandKey()}
                hasTags={hasTags} />;
        }

        if (taskId === enrolmentTask.ENROL_HAND.id) {
            return <EnrolHandWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                availabilityResolved={availabilityResolved}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                masterId={currentItem.id}
                selectedBiometricBrand={getSelectedBiometricBrandKey()} />;
        }

        if (taskId === enrolmentTask.ENROL_FACE.id) {
            return <EnrolFaceWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                availabilityResolved={availabilityResolved}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                pluginWSInstance={pluginWSInstance}
                hasTags={hasTags}
                masterId={currentItem.id}
                selectedBiometricBrand={getSelectedBiometricBrandKey()} />;
        }

        if (taskId === enrolmentTask.ENCODE_CARD.id) {
            return <EncodeCardWizard
                cardInfo={currentCredential}
                defaultContent={defaultContent}
                defaultSteps={steps}
                onBack={resetKeyField}
                onClose={closeWizard}
                onSave={onCardSave}
                availablePlugins={pluginsAvailable}
                availabilityResolved={availabilityResolved}
                availableSystemDeviceIds={availableSystemDeviceIds} />;
        }

        return <Wizard
            name={translate.byKey(TranslationKeys.enrollCredential)}
            step={taskSelectionStep}
            onClose={closeWizard}
            sidebarSteps={steps}>
            {taskSelectionContent}
        </Wizard>;
    };

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <WSPluginService
            onMessage={(value) => {
                if (value !== undefined && isPluginValidationListInterface(value)) {
                    const validPlugins = checkPluginValidatedList(value) as IShowEnrollValidOptions;
                    setPluginsAvailable({ ...defaultPluginsAvailable, ...validPlugins });
                    setIsPluginAvailabilityLoaded(true);
                }
            }}
            onOpen={(instance) => {
                setPluginWSInstance(instance);
                if (instance && instance.readyState === WebSocket.OPEN) {
                    instance.send(validatePluginMsg());
                    refreshDeviceAvailability();
                }
            }}
            onError={() => {
                setPluginWSInstance(undefined);
                setPluginsAvailable(defaultPluginsAvailable);
                setIsPluginAvailabilityLoaded(true);
            }}
            onClosed={() => {
                setPluginWSInstance(undefined);
                setIsPluginAvailabilityLoaded(true);
            }}
            url={window.location.protocol === "https:" ? WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET : WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET} />
        {getWizard()}
    </Modal>;
};

export default EnrolWizard;
